import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Check, ArrowRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";

// Pricing v2 — Free Viewer + Pro subscription. Monthly $9.99 / Yearly $79.
// The $1 lifetime tier was retired in Feb 2026 — see /memory/PRD.md for why.

const FREE_FEATURES = [
    "Open & view any CSV (any size)",
    "Search any column in milliseconds",
    "Share read-only links",
    "Download the original file",
    "Works on every device",
    "Mobile, tablet, desktop",
];

const PRO_FEATURES = [
    "Everything in Free, plus:",
    "Edit cells, add/remove rows & columns",
    "Save to the cloud — sync across devices",
    "All 9 Export Presets (Asana, Excel, Trello…)",
    "Share with edit permissions",
    "AI cleanup (coming soon)",
    "Receipt → CSV via AI (coming soon)",
    "Priority support",
    "Cancel anytime · 30-day money-back",
];

export default function Pricing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(null); // 'monthly' | 'yearly' | 'trial' | null

    const subscribe = async (plan) => {
        if (!user) {
            // route to signup, preserving intent so post-signup we can resume
            navigate(`/signup?next=/pricing&plan=${plan}`);
            return;
        }
        if (user.is_paid && user.subscription_status === "active") {
            navigate("/app");
            return;
        }
        try {
            setLoading(plan);
            const { data } = await api.post("/payments/subscription-checkout", {
                origin_url: window.location.origin,
                plan,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
            setLoading(null);
        }
    };

    const startTrial = async () => {
        if (!user) {
            navigate("/signup?next=/pricing&trial=1");
            return;
        }
        if (user.is_paid) {
            navigate("/app");
            return;
        }
        try {
            setLoading("trial");
            await api.post("/account/start-trial");
            toast.success("14-day Pro trial started — go edit!");
            // Force a fresh user fetch so navbar/editor refresh
            window.location.href = "/app";
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start trial");
            setLoading(null);
        }
    };

    const isPro = !!user && user.is_paid;

    return (
        <>
            <Helmet><title>Pricing — OpenCSV</title></Helmet>
            <section className="border-b border-black/10">
                <div className="mx-auto max-w-5xl px-4 md:px-8 py-20 md:py-24 text-center">
                    <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-4">/ pricing</div>
                    <h1 className="font-display font-black tracking-tighter text-5xl md:text-7xl">
                        Free to view. <span className="text-cobalt">$9.99 to edit anywhere.</span>
                    </h1>
                    <p className="mt-5 text-ink-soft max-w-2xl mx-auto text-base md:text-lg">
                        Open any CSV in seconds — that's free, forever. Need to edit, save, share, or run the export presets?
                        Upgrade to Pro. Cancel anytime, 30-day money-back guarantee, no questions.
                    </p>
                </div>
            </section>

            <section className="border-b border-black/10 bg-paper">
                <div className="mx-auto max-w-6xl px-4 md:px-8 py-14 grid md:grid-cols-2 gap-6">
                    {/* Free tier */}
                    <div className="border border-ink/15 bg-white p-8 md:p-10 flex flex-col" data-testid="pricing-card-free">
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted">OpenCSV — Free</div>
                        <div className="mt-3 flex items-baseline gap-2">
                            <span className="font-display font-black text-6xl tracking-tighter">$0</span>
                            <span className="text-ink-soft text-sm">forever</span>
                        </div>
                        <p className="mt-3 text-sm text-ink-soft">Open and view CSVs in your browser. No account needed.</p>
                        <ul className="mt-6 space-y-2 text-sm flex-1">
                            {FREE_FEATURES.map((t) => (
                                <li key={t} className="flex items-start gap-2"><Check size={14} className="text-ink-muted mt-0.5 flex-shrink-0" /> <span>{t}</span></li>
                            ))}
                        </ul>
                        <Link
                            to="/app"
                            className="btn-brutal w-full mt-7 bg-white text-ink border border-ink px-6 py-3 ui-heading font-semibold inline-flex items-center justify-center gap-2"
                            data-testid="pricing-free-cta"
                        >
                            Open the free viewer <ArrowRight size={16} />
                        </Link>
                    </div>

                    {/* Pro tier */}
                    <div className="border-2 border-cobalt bg-white p-8 md:p-10 shadow-brutal relative flex flex-col" data-testid="pricing-card-pro">
                        <span className="absolute -top-3 left-6 bg-cobalt text-white px-3 py-1 ui-heading text-[11px] uppercase tracking-widest inline-flex items-center gap-1">
                            <Sparkles size={11} /> Pro
                        </span>
                        <div className="ui-heading text-xs uppercase tracking-widest text-cobalt">OpenCSV Pro</div>
                        <div className="mt-3 flex items-baseline gap-2">
                            <span className="font-display font-black text-6xl tracking-tighter">$9.99</span>
                            <span className="text-ink-soft text-sm">/ month USD</span>
                        </div>
                        <p className="mt-1 text-sm text-ink-soft">or <span className="font-semibold text-ink">$79 / year</span> — save 34%.</p>
                        <ul className="mt-6 space-y-2 text-sm flex-1">
                            {PRO_FEATURES.map((t, i) => (
                                <li key={t} className={`flex items-start gap-2 ${i === 0 ? "font-semibold text-ink" : ""}`}>
                                    {i === 0 ? null : <Check size={14} className="text-cobalt mt-0.5 flex-shrink-0" />}
                                    <span className={i === 0 ? "ml-0" : ""}>{t}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="mt-7 space-y-2">
                            {!isPro && (
                                <button
                                    onClick={startTrial}
                                    disabled={!!loading}
                                    className="btn-brutal w-full bg-cobalt text-white border border-cobalt px-6 py-3 ui-heading font-semibold disabled:opacity-60 inline-flex items-center justify-center gap-2"
                                    data-testid="pricing-start-trial-button"
                                >
                                    {loading === "trial" ? "Starting…" : "Start 14-day free trial"} <ArrowRight size={16} />
                                </button>
                            )}
                            <div className="grid grid-cols-2 gap-2">
                                <button
                                    onClick={() => subscribe("monthly")}
                                    disabled={!!loading || (isPro && user?.subscription_status === "active")}
                                    className="btn-brutal bg-ink text-white px-4 py-2.5 ui-heading text-sm font-semibold disabled:opacity-60"
                                    data-testid="pricing-buy-monthly"
                                >
                                    {loading === "monthly" ? "…" : "Subscribe monthly"}
                                </button>
                                <button
                                    onClick={() => subscribe("yearly")}
                                    disabled={!!loading || (isPro && user?.subscription_status === "active")}
                                    className="btn-brutal bg-ink text-white px-4 py-2.5 ui-heading text-sm font-semibold disabled:opacity-60"
                                    data-testid="pricing-buy-yearly"
                                >
                                    {loading === "yearly" ? "…" : "Subscribe yearly"}
                                </button>
                            </div>
                        </div>
                        <p className="mt-3 text-[11px] text-ink-muted text-center">
                            No card needed for the 14-day trial. Cancel anytime from your account.
                        </p>
                    </div>
                </div>
            </section>

            <section className="border-b border-black/10 bg-white">
                <div className="mx-auto max-w-4xl px-4 md:px-8 py-16">
                    <h2 className="font-display font-black tracking-tighter text-3xl md:text-4xl">
                        Why <span className="text-cobalt">subscription</span>, not lifetime?
                    </h2>
                    <div className="grid md:grid-cols-2 gap-10 mt-8">
                        <div className="space-y-4 text-ink-soft text-base leading-relaxed">
                            <p>
                                Lifetime pricing kills indie products. A $1 lifetime customer costs us money to host forever
                                while we get paid once — that math doesn't keep the lights on.
                            </p>
                            <p>
                                A monthly subscription means we can keep improving OpenCSV, add real AI features, and not vanish
                                in 18 months when the bills outpace the revenue. <span className="font-semibold text-ink">Cancel anytime,
                                full refund within 30 days</span> — no questions, no friction.
                            </p>
                            <p className="text-sm">
                                If you bought $1 lifetime access in our early days, you're grandfathered in forever. Thank you for funding the rebuild.
                            </p>
                        </div>
                        <div className="border border-ink/15 p-6 bg-paper">
                            <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-3">/ what your subscription funds</div>
                            <ul className="space-y-3 text-sm">
                                <li className="flex justify-between border-b border-ink/10 pb-3"><span>Cloud storage + sync infra</span><span className="font-mono">~$1.20/mo</span></li>
                                <li className="flex justify-between border-b border-ink/10 pb-3"><span>AI features (when you use them)</span><span className="font-mono">~$0.45/mo</span></li>
                                <li className="flex justify-between border-b border-ink/10 pb-3"><span>Stripe + payment fees</span><span className="font-mono">~$0.60/mo</span></li>
                                <li className="flex justify-between border-b border-ink/10 pb-3"><span>Ongoing dev & support</span><span className="font-mono">~$5.50/mo</span></li>
                                <li className="flex justify-between text-ink"><span className="ui-heading">Margin → next features</span><span className="font-mono">~$2.24/mo</span></li>
                            </ul>
                            <p className="text-xs text-ink-muted mt-4">No ads, no data harvesting, no upsells. Your money funds the product directly.</p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
}
